// YNX BUSINESS API CLIENT
// ───────────────────────────────────────────────────────────────
// All B2B endpoints. Sends `X-YNX-Workspace` automatically when a
// workspace id is stored locally (key: ynx_workspace_id).
// ───────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_BACKEND_URL;

const wsId = () => {
  try { return localStorage.getItem('ynx_workspace_id') || ''; }
  catch (err) { console.warn('YNX biz: localStorage read failed', err); return ''; }
};

const headers = (extra = {}) => ({
  'Content-Type': 'application/json',
  ...(wsId() ? { 'X-YNX-Workspace': wsId() } : {}),
  ...extra,
});

const json = async (res) => {
  if (!res.ok) {
    let err = `HTTP ${res.status}`;
    // Intentional fallback: if body isn't JSON we keep the HTTP code as error message.
    try { const j = await res.json(); err = j.detail || err; }
    catch (e) { console.debug('YNX biz: error body not JSON', e); }
    throw new Error(err);
  }
  return res.json();
};

// ─── WORKSPACE ───
export const createWorkspace = (payload) =>
  fetch(`${API}/api/biz/workspace`, { method: 'POST', headers: headers(), body: JSON.stringify(payload) }).then(json);

export const joinWorkspace = (code) =>
  fetch(`${API}/api/biz/workspace/join`, { method: 'POST', headers: headers(), body: JSON.stringify({ code }) }).then(json);

// Recover the list of workspaces owned by a given email.
// Rate-limited server-side: 8 attempts per email per 24h.
export const recoverByEmail = (email) =>
  fetch(`${API}/api/biz/workspace/recover_by_email`, {
    method: 'POST', headers: headers(), body: JSON.stringify({ email }),
  }).then(json);

export const getWorkspace = (id) =>
  fetch(`${API}/api/biz/workspace/${id}`, { headers: headers() }).then(json);

export const updateWorkspace = (id, payload) =>
  fetch(`${API}/api/biz/workspace/${id}`, { method: 'PUT', headers: headers(), body: JSON.stringify(payload) }).then(json);

export const rotateWorkspaceCode = (id) =>
  fetch(`${API}/api/biz/workspace/${id}/rotate-code`, { method: 'POST', headers: headers() }).then(json);

// ─── CLIENTS ───
export const listClients = () =>
  fetch(`${API}/api/biz/clients`, { headers: headers() }).then(json);
export const createClient = (c) =>
  fetch(`${API}/api/biz/clients`, { method: 'POST', headers: headers(), body: JSON.stringify(c) }).then(json);
export const updateClient = (id, c) =>
  fetch(`${API}/api/biz/clients/${id}`, { method: 'PUT', headers: headers(), body: JSON.stringify(c) }).then(json);
export const deleteClient = (id) =>
  fetch(`${API}/api/biz/clients/${id}`, { method: 'DELETE', headers: headers() }).then(json);

// ─── INVOICES ───
export const listInvoices = (type) => {
  const q = type ? `?type=${type}` : '';
  return fetch(`${API}/api/biz/invoices${q}`, { headers: headers() }).then(json);
};
export const createInvoice = (inv) =>
  fetch(`${API}/api/biz/invoices`, { method: 'POST', headers: headers(), body: JSON.stringify(inv) }).then(json);
export const updateInvoice = (id, inv) =>
  fetch(`${API}/api/biz/invoices/${id}`, { method: 'PUT', headers: headers(), body: JSON.stringify(inv) }).then(json);
export const deleteInvoice = (id) =>
  fetch(`${API}/api/biz/invoices/${id}`, { method: 'DELETE', headers: headers() }).then(json);

// ─── APPOINTMENTS ───
export const listAppointments = () =>
  fetch(`${API}/api/biz/appointments`, { headers: headers() }).then(json);
export const createAppointment = (a) =>
  fetch(`${API}/api/biz/appointments`, { method: 'POST', headers: headers(), body: JSON.stringify(a) }).then(json);
export const updateAppointment = (id, a) =>
  fetch(`${API}/api/biz/appointments/${id}`, { method: 'PUT', headers: headers(), body: JSON.stringify(a) }).then(json);
export const deleteAppointment = (id) =>
  fetch(`${API}/api/biz/appointments/${id}`, { method: 'DELETE', headers: headers() }).then(json);

// ─── TASKS ───
export const listTasks = () =>
  fetch(`${API}/api/biz/tasks`, { headers: headers() }).then(json);
export const createTask = (t) =>
  fetch(`${API}/api/biz/tasks`, { method: 'POST', headers: headers(), body: JSON.stringify(t) }).then(json);
export const updateTask = (id, t) =>
  fetch(`${API}/api/biz/tasks/${id}`, { method: 'PUT', headers: headers(), body: JSON.stringify(t) }).then(json);
export const deleteTask = (id) =>
  fetch(`${API}/api/biz/tasks/${id}`, { method: 'DELETE', headers: headers() }).then(json);

// ─── CONTACTS (annuaire interne) ───
export const listContacts = () =>
  fetch(`${API}/api/biz/contacts`, { headers: headers() }).then(json);
export const createContact = (c) =>
  fetch(`${API}/api/biz/contacts`, { method: 'POST', headers: headers(), body: JSON.stringify(c) }).then(json);
export const updateContact = (id, c) =>
  fetch(`${API}/api/biz/contacts/${id}`, { method: 'PUT', headers: headers(), body: JSON.stringify(c) }).then(json);
export const deleteContact = (id) =>
  fetch(`${API}/api/biz/contacts/${id}`, { method: 'DELETE', headers: headers() }).then(json);

// ─── INTERNAL MESSAGES ───
export const listMessages = (extension) => {
  const q = extension ? `?extension=${encodeURIComponent(extension)}` : '';
  return fetch(`${API}/api/biz/messages${q}`, { headers: headers() }).then(json);
};
export const createMessage = (m) =>
  fetch(`${API}/api/biz/messages`, { method: 'POST', headers: headers(), body: JSON.stringify(m) }).then(json);
export const markMessageRead = (id) =>
  fetch(`${API}/api/biz/messages/${id}/read`, { method: 'PUT', headers: headers() }).then(json);
export const deleteMessage = (id) =>
  fetch(`${API}/api/biz/messages/${id}`, { method: 'DELETE', headers: headers() }).then(json);

// ─── SUPPORT ───
export const createSupportTicket = (t) =>
  fetch(`${API}/api/biz/support`, { method: 'POST', headers: headers(), body: JSON.stringify(t) }).then(json);

// ─── WORD EXPORT ───
// Returns a blob (Microsoft Word .docx). Caller is responsible for downloading.
export const downloadInvoiceDocx = async (invoiceId, filename = 'document.docx') => {
  const res = await fetch(`${API}/api/biz/invoices/${invoiceId}/docx`, { headers: headers() });
  if (!res.ok) {
    let err = `HTTP ${res.status}`;
    try { const j = await res.json(); err = j.detail || err; }
    catch (e) { console.debug('YNX biz: docx error body not JSON', e); }
    throw new Error(err);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ─── LOCAL WORKSPACE STORE ───
export const setLocalWorkspace = (id) => {
  try { localStorage.setItem('ynx_workspace_id', id || ''); }
  catch (err) { console.warn('YNX biz: localStorage write failed', err); }
};
export const getLocalWorkspace = () => {
  try { return localStorage.getItem('ynx_workspace_id') || ''; }
  catch (err) { console.warn('YNX biz: localStorage read failed', err); return ''; }
};
export const clearLocalWorkspace = () => {
  try { localStorage.removeItem('ynx_workspace_id'); }
  catch (err) { console.warn('YNX biz: localStorage clear failed', err); }
};
